import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Collapse,
  IconButton,
  Avatar,
  Stack,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

/**
 * MobileDataCard - Tarjeta expandible para vista móvil
 *
 * @param {string} title - Título principal (nombre, evento, etc.)
 * @param {string} subtitle - Subtítulo secundario
 * @param {string} avatar - URL del avatar o iniciales
 * @param {string} avatarColor - Color del avatar si no hay imagen
 * @param {object} status - { label, color } para mostrar un chip de estado
 * @param {Array} primaryInfo - [{ icon, label, value }] información principal visible siempre
 * @param {Array} expandedInfo - [{ icon, label, value }] información que se muestra al expandir
 * @param {Array} actions - Botones de acción [{ icon, label, onClick, color }]
 * @param {Array} customActions - Nodos React adicionales renderizados en la zona de acciones
 * @param {function} onClick - Handler al hacer click en la tarjeta
 * @param {boolean} defaultExpanded - Si inicia expandida
 */
const MobileDataCard = ({
  title,
  subtitle,
  avatar,
  avatarColor = "#1976d2",
  status,
  primaryInfo = [],
  expandedInfo = [],
  actions = [],
  customActions = [],
  onClick,
  defaultExpanded = false,
  sx = {},
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleToggleExpand = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  };

  const renderInfoItem = ({ icon, label, value }, index) => {
    if (!value && value !== 0) return null;

    return (
      <Box
        key={index}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          py: 0.5,
        }}
      >
        {icon && (
          <Box sx={{ color: "rgba(255,255,255,0.45)", display: "flex", mt: 0.25 }}>{icon}</Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              color: "rgba(255,255,255,0.45)",
              display: "block",
              fontSize: "0.68rem",
              lineHeight: 1.2,
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: "#fff",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: "0.9rem",
            }}
          >
            {value}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Card
      onClick={onClick}
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        border: "1px solid",
        borderColor: expanded ? "primary.main" : "rgba(255,255,255,0.08)",
        background: "linear-gradient(145deg, #1a1f2e 0%, #131829 100%)",
        boxShadow: expanded
          ? "0 8px 32px rgba(0,0,0,0.35)"
          : "0 2px 12px rgba(0,0,0,0.25)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: onClick ? "pointer" : "default",
        width: "100%",
        boxSizing: "border-box",
        "&:hover": {
          borderColor: "primary.light",
          transform: "translateY(-1px)",
          boxShadow: "0 6px 24px rgba(0,0,0,0.35)",
        },
        "&:active": {
          transform: "scale(0.98)",
        },
        ...sx,
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        {/* Header - Siempre visible */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Avatar */}
          <Avatar
            src={avatar}
            sx={{
              width: 52,
              height: 52,
              bgcolor: avatarColor,
              fontSize: "1.1rem",
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {!avatar && getInitials(title)}
          </Avatar>

          {/* Título y subtítulo */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: "#fff",
                lineHeight: 1.25,
                fontSize: "1rem",
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.55)", display: "block", mt: 0.25 }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          {/* Status chip - estilo pill button */}
          {status && (
            <Box
              sx={{
                px: 1.75,
                py: 0.5,
                borderRadius: 20,
                bgcolor: status.color || "#1976d2",
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.78rem",
                whiteSpace: "nowrap",
                flexShrink: 0,
                letterSpacing: 0.2,
              }}
            >
              {status.label}
            </Box>
          )}

          {/* Botón expandir */}
          {(expandedInfo.length > 0 || actions.length > 0 || customActions.length > 0) && (
            <IconButton
              onClick={handleToggleExpand}
              size="small"
              sx={{
                ml: 0.25,
                color: "rgba(255,255,255,0.6)",
                transition: "transform 0.3s",
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          )}
        </Box>

        {/* Información primaria - siempre visible */}
        {primaryInfo.length > 0 && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 1,
              mt: 2,
              pt: 2,
              borderTop: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            {primaryInfo.map(renderInfoItem)}
          </Box>
        )}

        {/* Contenido expandible */}
        <Collapse in={expanded} timeout={300}>
          {expandedInfo.length > 0 && (
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: "1px dashed rgba(255,255,255,0.1)",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255,255,255,0.35)",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  mb: 1,
                  display: "block",
                }}
              >
                Detalles
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {expandedInfo.map(renderInfoItem)}
              </Box>
            </Box>
          )}

          {/* Acciones */}
          {(actions.length > 0 || customActions.length > 0) && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                mt: 2,
                pt: 2,
                borderTop: "1px solid rgba(255,255,255,0.08)",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              {customActions.map((node, index) => (
                <Box key={`custom-${index}`} onClick={(e) => e.stopPropagation()}>
                  {node}
                </Box>
              ))}
              {actions.map((action, index) => (
                <IconButton
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick?.();
                  }}
                  size="small"
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: action.color === "error"
                      ? "rgba(244,67,54,0.18)"
                      : "rgba(255,255,255,0.08)",
                    color: action.color === "error"
                      ? "#f44336"
                      : "rgba(255,255,255,0.8)",
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor: action.color === "error"
                        ? "rgba(244,67,54,0.32)"
                        : "rgba(255,255,255,0.18)",
                      transform: "scale(1.1)",
                    },
                  }}
                  title={action.label}
                >
                  {action.icon}
                </IconButton>
              ))}
            </Stack>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

/**
 * MobileDataList - Contenedor de lista de tarjetas móviles
 */
export const MobileDataList = ({
  children,
  loading = false,
  emptyMessage = "No hay datos para mostrar",
  emptyIcon,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
        {[1, 2, 3].map((i) => (
          <Card
            key={i}
            sx={{
              height: 100,
              borderRadius: 3,
              animation: "pulse 1.5s ease-in-out infinite",
              "@keyframes pulse": {
                "0%": { opacity: 0.6 },
                "50%": { opacity: 0.3 },
                "100%": { opacity: 0.6 },
              },
            }}
          />
        ))}
      </Box>
    );
  }

  const childrenArray = React.Children.toArray(children);

  if (childrenArray.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
          px: 2,
          textAlign: "center",
        }}
      >
        {emptyIcon && (
          <Box sx={{ color: "text.disabled", mb: 2, fontSize: 48 }}>
            {emptyIcon}
          </Box>
        )}
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        p: 1.5,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {children}
    </Box>
  );
};

export default MobileDataCard;
